/**
 * AgentOsDemoProductImporter — V140 P1.
 *
 * Public sandbox for the Product Importer agent. Accepts either a
 * remote feed URL or pasted XML/CSV text, auto-detects the format
 * (Heureka XML / Google Shopping XML / generic CSV), and renders a
 * normalized table of products ready to push to WooCommerce/Shopify.
 *
 * Global by design — every locale is supported by the same generic
 * parser. The LLM-enhancement toggle uses gpt-4o-mini in 1 of 10
 * default-supported languages.
 *
 * Uses the AgentOsDemoShell `{testid, h1, lead, left, right}` contract,
 * placing the form on the left and the results table on the right.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Loader2, FileText, Globe, Upload, CheckCircle, Send } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const LANGS = [
  { id: "en", name: "English" }, { id: "sk", name: "Slovenčina" },
  { id: "cs", name: "Čeština" }, { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" }, { id: "hu", name: "Magyar" },
  { id: "fr", name: "Français" }, { id: "es", name: "Español" },
  { id: "it", name: "Italiano" }, { id: "pt", name: "Português" },
];

const EXAMPLE_CSV = `name,price,currency,brand,category,description
Wireless Headphones X3,89.99,EUR,Sony,Electronics,Studio-grade bluetooth headphones with 40h battery.
Drill Bosch GSB18V,149.50,EUR,Bosch,Tools,Cordless 18V combi drill with 2 batteries.
Eco Cotton T-Shirt,24.00,EUR,GreenWear,Clothing,Organic cotton t-shirt — global shipping.`;
const EXAMPLE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<SHOP>
  <SHOPITEM>
    <ITEM_ID>SKU-001</ITEM_ID>
    <PRODUCTNAME>Bluetooth slúchadlá BoomBass X3</PRODUCTNAME>
    <DESCRIPTION>Bezdrôtové slúchadlá s 40h výdržou batérie.</DESCRIPTION>
    <PRICE_VAT>89.99</PRICE_VAT>
    <CURRENCY>EUR</CURRENCY>
    <MANUFACTURER>BoomBass</MANUFACTURER>
    <CATEGORYTEXT>Elektronika</CATEGORYTEXT>
  </SHOPITEM>
</SHOP>`;

export default function AgentOsDemoProductImporter() {
  const [mode, setMode] = useState("text");   // 'text' | 'url'
  const [feedText, setFeedText] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [enhance, setEnhance] = useState(false);
  const [lang, setLang] = useState("en");
  const [brandVoice] = useState("friendly");
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  // Push-to-shop (V140.5)
  const [pushWorkspaceId, setPushWorkspaceId] = useState("");
  const [pushPlatform, setPushPlatform] = useState("woocommerce");
  const [pushMode, setPushMode] = useState("create_only");
  const [pushResult, setPushResult] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "text" && !feedText.trim()) return;
    if (mode === "url" && !feedUrl.trim()) return;
    setStatus({ loading: true, error: null });
    setResult(null);
    try {
      const payload = {
        ...(mode === "text" ? { feed_text: feedText } : { feed_url: feedUrl }),
        enhance, lang, brand_voice: brandVoice, max_products: 50,
      };
      const r = await axios.post(`${API}/agent-os/demo/product-importer`, payload);
      setResult(r.data);
      setStatus({ loading: false, error: null });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        loading: false,
        error: typeof detail === "string" ? detail : (detail?.message || "import_failed"),
      });
    }
  };

  const pushToShop = async () => {
    if (!result?.products?.length || !pushWorkspaceId.trim()) return;
    setPushBusy(true); setPushResult(null);
    try {
      const r = await axios.post(`${API}/eshop-sync/push`, {
        workspace_id: pushWorkspaceId.trim(),
        platform: pushPlatform,
        mode: pushMode,
        products: result.products,
      });
      setPushResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPushResult({
        ok: false,
        error: typeof d === "string" ? d : (d?.message || "push_failed"),
      });
    }
    setPushBusy(false);
  };

  const form = (
    <form
      onSubmit={submit}
      className="space-y-5 p-6 bg-neutral-950 border border-gray-800"
      data-testid="product-importer-form"
    >
      {/* Mode picker */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { id: "text", label: "Vlož XML / CSV", Icon: FileText },
          { id: "url",  label: "Z URL feedu",     Icon: Globe },
        ].map(({ id, label, Icon }) => (
          <button
            type="button"
            key={id}
            data-testid={`importer-mode-${id}`}
            onClick={() => setMode(id)}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-bold border ${
              mode === id ? "border-emerald-400 bg-emerald-500/10 text-emerald-300" : "border-gray-800 text-gray-400 hover:border-gray-700"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Input */}
      {mode === "text" ? (
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
            Feed (XML alebo CSV)
          </label>
          <textarea
            data-testid="importer-feed-text"
            value={feedText}
            onChange={(e) => setFeedText(e.target.value)}
            rows={10}
            spellCheck="false"
            placeholder="Vlož XML alebo CSV..."
            className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-xs font-mono focus:outline-none focus:border-emerald-400"
          />
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => setFeedText(EXAMPLE_CSV)}
              className="text-[11px] text-emerald-400 hover:text-emerald-300" data-testid="importer-example-csv">
              ↳ Príklad CSV
            </button>
            <button type="button" onClick={() => setFeedText(EXAMPLE_XML)}
              className="text-[11px] text-emerald-400 hover:text-emerald-300" data-testid="importer-example-xml">
              ↳ Príklad Heureka XML
            </button>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
            URL feedu
          </label>
          <input
            data-testid="importer-feed-url"
            value={feedUrl}
            onChange={(e) => setFeedUrl(e.target.value)}
            type="url"
            placeholder="https://shop.example.com/feed.xml"
            className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Podporuje: Heureka XML, Google Shopping RSS, generický CSV (do 5 MB).
          </p>
        </div>
      )}

      {/* Enhance + language */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer" data-testid="importer-enhance-toggle">
          <input
            type="checkbox"
            checked={enhance}
            onChange={(e) => setEnhance(e.target.checked)}
            className="w-4 h-4 accent-emerald-400"
          />
          AI prepíš popisy (gpt-4o-mini) — prvých 5 produktov
        </label>
        <select
          data-testid="importer-lang"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 disabled:opacity-40"
          disabled={!enhance}
        >
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <button
        type="submit"
        disabled={status.loading || (mode === "text" ? !feedText.trim() : !feedUrl.trim())}
        data-testid="importer-submit"
        className="w-full bg-emerald-400 hover:bg-emerald-300 text-black font-black py-3 text-[12px] uppercase tracking-widest disabled:opacity-40"
      >
        {status.loading ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Upload className="w-4 h-4 inline mr-2" />}
        {status.loading ? "Importuje sa..." : "Importovať feed"}
      </button>

      {status.error && (
        <p className="text-sm text-red-400" data-testid="importer-error">⚠️ {status.error}</p>
      )}
    </form>
  );

  const output = (
    <div className="p-6 bg-neutral-950 border border-gray-800" data-testid="importer-output">
      {!result && !status.loading && (
        <div className="text-center text-gray-500 py-16">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Vlož feed a uvidíš normalizované produkty pripravené na import do WooCommerce / Shopify.</p>
        </div>
      )}
      {status.loading && (
        <div className="text-center text-gray-400 py-16">
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-emerald-400" />
          <p className="text-sm">Parsujem feed…</p>
        </div>
      )}
      {result && (
        <div className="space-y-4" data-testid="importer-result">
          <div className="flex items-center justify-between p-3 bg-emerald-950/30 border border-emerald-500/30">
            <p className="text-sm text-emerald-300">
              <CheckCircle className="w-4 h-4 inline mr-2" />
              <strong>{result.products_count}</strong> produktov ako <code className="text-amber-300">{result.detected_format}</code>
              {result.enhanced_count > 0 && (
                <span className="ml-2 text-[11px]">· AI prepísaných: {result.enhanced_count}</span>
              )}
            </p>
          </div>

          <div className="border border-gray-800 overflow-x-auto" data-testid="importer-products-table">
            <table className="w-full text-xs">
              <thead className="bg-neutral-900 text-emerald-400">
                <tr>
                  <th className="px-3 py-2 text-left font-black uppercase tracking-wider">Názov</th>
                  <th className="px-3 py-2 text-left font-black uppercase tracking-wider">Brand</th>
                  <th className="px-3 py-2 text-right font-black uppercase tracking-wider">Cena</th>
                </tr>
              </thead>
              <tbody>
                {result.products.map((p, i) => (
                  <tr key={i} className="border-t border-gray-900 hover:bg-neutral-900/50">
                    <td className="px-3 py-2 text-gray-200">
                      {p.name}
                      {p.description_ai && (
                        <p className="text-[10px] text-emerald-400 mt-1 italic">AI: {p.description_ai.slice(0, 100)}…</p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-400">{p.brand || "—"}</td>
                    <td className="px-3 py-2 text-right text-gray-200 tabular-nums font-bold">
                      {p.price ? `${Number(p.price).toFixed(2)} ${p.currency || "EUR"}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="border border-gray-800 p-3 text-xs">
            <summary className="cursor-pointer text-gray-400 font-bold">Raw JSON (pre vývojárov)</summary>
            <pre className="mt-2 text-[10px] text-gray-500 overflow-x-auto">
              {JSON.stringify(result.products, null, 2)}
            </pre>
          </details>

          {/* V140.5 — Push to my shop */}
          <div className="border border-amber-500/30 bg-amber-950/10 p-3 space-y-2" data-testid="importer-push-panel">
            <p className="text-[11px] text-amber-300 font-black uppercase tracking-widest">
              <Send className="w-3 h-3 inline mr-1.5" /> Pošli do môjho e-shopu
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input
                value={pushWorkspaceId}
                onChange={(e) => setPushWorkspaceId(e.target.value)}
                placeholder="workspace_id (ws_...)"
                data-testid="importer-push-workspace"
                className="bg-black border border-gray-800 text-gray-100 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-amber-500"
              />
              <select
                value={pushPlatform}
                onChange={(e) => setPushPlatform(e.target.value)}
                data-testid="importer-push-platform"
                className="bg-black border border-gray-800 text-gray-100 px-2 py-1.5 text-xs focus:outline-none focus:border-amber-500"
              >
                <option value="woocommerce">WooCommerce</option>
                <option value="shopify">Shopify</option>
                <option value="prestashop">PrestaShop</option>
                <option value="magento">Magento</option>
                <option value="opencart">OpenCart</option>
              </select>
              <select
                value={pushMode}
                onChange={(e) => setPushMode(e.target.value)}
                data-testid="importer-push-mode"
                className="bg-black border border-gray-800 text-gray-100 px-2 py-1.5 text-xs focus:outline-none focus:border-amber-500"
                title="create_only = skip duplicates · upsert = update existing"
              >
                <option value="create_only">Create only (skip dup)</option>
                <option value="upsert">Upsert (update if exists)</option>
              </select>
              <button
                onClick={pushToShop}
                disabled={pushBusy || !pushWorkspaceId.trim()}
                data-testid="importer-push-submit"
                className="bg-amber-500 text-black font-black py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40"
              >
                {pushBusy ? "Tlačím…" : "Push do shopu"}
              </button>
            </div>
            {pushResult && (
              <div className="text-xs" data-testid="importer-push-result">
                {pushResult.ok ? (
                  <p className="text-emerald-400">
                    ✓ {pushResult.created} vytvorených · {pushResult.updated || 0} aktualizovaných · {pushResult.skipped} preskočených · {pushResult.failed} zlyhalo
                  </p>
                ) : (
                  <p className="text-red-400">⚠️ {pushResult.error}</p>
                )}
              </div>
            )}
            <p className="text-[10px] text-gray-500">
              Workspace musí mať pripojený e-shop (Dashboard → E-shop integrácie). Produkty sa vytvoria ako <strong>draft</strong> — publish manuálne.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-demo-product-importer"
      h1="Product Importer Agent"
      lead="Auto-import produktov z XML / CSV / Google Shopping feedov do WooCommerce, Shopify, PrestaShop. Globálne — bez ohľadu na jazyk feedu."
      left={form}
      right={output}
    />
  );
}
